import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import LocalCarWashIcon from '@mui/icons-material/LocalCarWash';
import PlaceIcon from '@mui/icons-material/Place';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsIcon from '@mui/icons-material/Directions';
import PhoneIcon from '@mui/icons-material/Phone';
import { NearbyPartner } from '../../../types/additionalServiceTypes';
import ContractServiceDialog from './ContractServiceDialog';

interface Props {
  partner: NearbyPartner;
  serviceName: string;
  serviceId: number | null;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder > 0 ? `${hours}h ${remainder}min` : `${hours}h`;
}

function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatAddress(partner: NearbyPartner): string {
  const { neighborhood, city, state } = partner.address;
  const parts = [neighborhood, city, state].filter(Boolean);
  return parts.join(', ') || 'Endereço não informado';
}

function buildMapsUrl(partner: NearbyPartner): string {
  const { latitude, longitude } = partner.address;
  return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
}

function PartnerCard({ partner, serviceName, serviceId }: Props) {
  const [contractOpen, setContractOpen] = useState(false);

  const hasPromotion = partner.original_price !== null && partner.original_price > partner.price;

  const timeLabel =
    partner.time
      ? `${partner.time}${partner.time_end ? ` – ${partner.time_end}` : ''}${partner.duration_minutes > 0 ? ` · ${formatDuration(partner.duration_minutes)}` : ''}`
      : partner.duration_minutes > 0
        ? formatDuration(partner.duration_minutes)
        : null;

  return (
    <>
      <Card
        sx={{
          borderRadius: '12px',
          boxShadow: 'none',
          border: '1px solid #E8EAF0',
          bgcolor: '#fff',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent sx={{ p: '20px', flex: 1, display: 'flex', flexDirection: 'column', '&:last-child': { pb: '20px' } }}>

          {/* Header: logo + name + distance + address */}
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Avatar
              src={partner.logo || undefined}
              alt={partner.carwash_name}
              variant="rounded"
              sx={{
                width: 56,
                height: 56,
                bgcolor: '#fff',
                border: '1.5px dashed #C0C8D8',
                flexShrink: 0,
                fontSize: 10,
                color: '#8a8a8a',
              }}
            >
              <LocalCarWashIcon sx={{ color: '#B0BAC9', fontSize: 28 }} />
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: '#2F3857',
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {partner.carwash_name}
                </Typography>
                <Chip
                  label={formatDistance(partner.distance_km)}
                  size="small"
                  sx={{ bgcolor: '#E3F4FD', color: '#00b4e4', fontWeight: 700, fontSize: 11, flexShrink: 0, height: 22 }}
                />
              </Stack>

              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.4 }}>
                <PlaceIcon sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {formatAddress(partner)}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          {/* Time + Price */}
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            {timeLabel && (
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary">
                  {timeLabel}
                </Typography>
              </Stack>
            )}

            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              {hasPromotion && (
                <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.disabled', display: 'block', lineHeight: 1.2 }}>
                  {formatCurrency(partner.original_price!)}
                </Typography>
              )}
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2F3857', lineHeight: 1.2 }}>
                {formatCurrency(partner.price)}
              </Typography>
              {hasPromotion && (
                <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 700 }}>
                  Promoção
                </Typography>
              )}
            </Box>
          </Stack>

          {/* Days */}
          {partner.week_days.length > 0 && (
            <Box sx={{ mt: 1.25 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Dias disponíveis
              </Typography>
              <Typography variant="caption" fontWeight={600} color="#2F3857">
                {partner.week_days.slice(0, 3).join(', ')}
                {partner.week_days.length > 3 && ` +${partner.week_days.length - 3}`}
              </Typography>
            </Box>
          )}

          {/* Actions */}
          <Stack spacing={1} sx={{ mt: 'auto', pt: 1.75 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setContractOpen(true)}
              sx={{
                bgcolor: '#12A9DC',
                '&:hover': { bgcolor: '#0d8fb8' },
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '8px',
                fontSize: 14,
                py: '13px',
              }}
            >
              Contratar
            </Button>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                component="a"
                href={buildMapsUrl(partner)}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<DirectionsIcon sx={{ fontSize: '16px !important', color: '#E83C3C' }} />}
                sx={{
                  flex: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  borderColor: '#E0E0E0',
                  color: '#2F3857',
                  fontSize: 13,
                  py: '10px',
                  minWidth: 0,
                }}
              >
                Como chegar
              </Button>

              {partner.phone && (
                <Button
                  variant="outlined"
                  component="a"
                  href={`tel:${partner.phone}`}
                  startIcon={<PhoneIcon sx={{ fontSize: '16px !important', color: '#E83C3C' }} />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '8px',
                    borderColor: '#E0E0E0',
                    color: '#2F3857',
                    fontSize: 13,
                    py: '10px',
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Ligar
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <ContractServiceDialog
        open={contractOpen}
        serviceName={serviceName}
        partnerName={partner.carwash_name}
        serviceId={serviceId}
        carwashId={partner.carwash_id ?? null}
        onClose={() => setContractOpen(false)}
      />
    </>
  );
}

export default PartnerCard;
